const DAY_MS = 24 * 60 * 60 * 1000;

export const STREAKS_GRACE_PERIOD_MS = 7 * DAY_MS;

export function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDate(input) {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === "number" || typeof input === "string") {
    const parsed = new Date(input);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function isValidTimeZone(timeZone) {
  if (typeof timeZone !== "string" || !timeZone.trim()) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch (err) {
    return false;
  }
}

export function resolveTimeZone(timeZone) {
  return isValidTimeZone(timeZone) ? timeZone : "UTC";
}

export function getZonedDateParts(dateInput, timeZoneInput = "UTC") {
  const date = toDate(dateInput);
  if (!date) {
    throw new Error("Invalid date input");
  }
  const timeZone = resolveTimeZone(timeZoneInput);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const day = Number(parts.find((part) => part.type === "day")?.value);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error("Unable to resolve zoned date parts");
  }

  return { year, month, day };
}

export function getIsoWeekFromYmd(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayIndex = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayIndex + 3);
  const isoYear = date.getUTCFullYear();

  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstThursdayDayIndex = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDayIndex + 3);

  const isoWeek = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
  return { isoYear, isoWeek };
}

export function getDayKeyForDate(dateInput, timeZoneInput = "UTC") {
  const { year, month, day } = getZonedDateParts(dateInput, timeZoneInput);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function getMonthKeyForDate(dateInput, timeZoneInput = "UTC") {
  const { year, month } = getZonedDateParts(dateInput, timeZoneInput);
  return `${year}-${pad2(month)}`;
}

export function getWeekKeyForDate(dateInput, timeZoneInput = "UTC") {
  const { year, month, day } = getZonedDateParts(dateInput, timeZoneInput);
  const { isoYear, isoWeek } = getIsoWeekFromYmd(year, month, day);
  return `${isoYear}-W${pad2(isoWeek)}`;
}

export function parseMonthKey(monthKey) {
  if (typeof monthKey !== "string") {
    return null;
  }
  const match = monthKey.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

export function parseDayKey(dayKey) {
  if (typeof dayKey !== "string") {
    return null;
  }
  const match = dayKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  return { year, month, day };
}

function monthKeyToUtcDate(monthKey) {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) {
    return null;
  }
  return new Date(Date.UTC(parsed.year, parsed.month - 1, 1, 12));
}

export function dayKeyToUtcDate(dayKey) {
  const parsed = parseDayKey(dayKey);
  if (!parsed) {
    return null;
  }
  return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12));
}

export function dayKeyFromUtcDate(date) {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

export function addMonthsToMonthKey(monthKey, months) {
  const date = monthKeyToUtcDate(monthKey);
  if (!date) {
    return null;
  }
  const offset = Number(months);
  date.setUTCMonth(date.getUTCMonth() + (Number.isFinite(offset) ? offset : 0));
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
}

export function addDaysToDayKey(dayKey, days) {
  const date = dayKeyToUtcDate(dayKey);
  if (!date) {
    return null;
  }
  date.setUTCDate(date.getUTCDate() + days);
  return dayKeyFromUtcDate(date);
}

export function parseWeekKey(weekKey) {
  if (typeof weekKey !== "string") {
    return null;
  }
  const match = weekKey.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1 || week > 53) {
    return null;
  }
  return { year, week };
}

function isoWeekToMondayDate(year, week) {
  const januaryFourth = new Date(Date.UTC(year, 0, 4));
  const januaryFourthDayIndex = (januaryFourth.getUTCDay() + 6) % 7;
  januaryFourth.setUTCDate(januaryFourth.getUTCDate() - januaryFourthDayIndex);
  januaryFourth.setUTCDate(januaryFourth.getUTCDate() + (week - 1) * 7);
  return januaryFourth;
}

export function weekKeyToMondayDate(weekKey) {
  const parsed = parseWeekKey(weekKey);
  if (!parsed) {
    return null;
  }
  return isoWeekToMondayDate(parsed.year, parsed.week);
}

function weekDateToWeekKey(date) {
  const { isoYear, isoWeek } = getIsoWeekFromYmd(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate()
  );
  return `${isoYear}-W${pad2(isoWeek)}`;
}

export function compareWeekKeys(a, b) {
  const aDate = weekKeyToMondayDate(a);
  const bDate = weekKeyToMondayDate(b);
  if (!aDate && !bDate) {
    return 0;
  }
  if (!aDate) {
    return -1;
  }
  if (!bDate) {
    return 1;
  }
  return aDate.getTime() - bDate.getTime();
}

export function getPreviousWeekKey(weekKey) {
  const monday = weekKeyToMondayDate(weekKey);
  if (!monday) {
    return null;
  }
  monday.setUTCDate(monday.getUTCDate() - 7);
  return weekDateToWeekKey(monday);
}

export function getNextWeekKey(weekKey) {
  const monday = weekKeyToMondayDate(weekKey);
  if (!monday) {
    return null;
  }
  monday.setUTCDate(monday.getUTCDate() + 7);
  return weekDateToWeekKey(monday);
}

export function getWeekStartDayKey(weekKey) {
  const monday = weekKeyToMondayDate(weekKey);
  if (!monday) {
    return null;
  }
  return dayKeyFromUtcDate(monday);
}

export function formatDayTitle(dayKey) {
  const date = dayKeyToUtcDate(dayKey);
  if (!date) {
    return dayKey || "";
  }
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatShortDayKey(dayKey) {
  const date = dayKeyToUtcDate(dayKey);
  if (!date) {
    return "";
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function formatMonthLabel(monthKey) {
  const date = monthKeyToUtcDate(monthKey);
  if (!date) {
    return monthKey || "";
  }
  return date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function getFiveWeekCalendar(timeZoneInput = "UTC", now = new Date()) {
  const timeZone = resolveTimeZone(timeZoneInput);
  const currentWeekKey = getWeekKeyForDate(now, timeZone);
  const todayKey = getDayKeyForDate(now, timeZone);

  const previousWeeks = [];
  let previousCursor = currentWeekKey;
  for (let i = 0; i < 2; i += 1) {
    previousCursor = getPreviousWeekKey(previousCursor);
    previousWeeks.unshift(previousCursor);
  }

  const nextWeeks = [];
  let nextCursor = currentWeekKey;
  for (let i = 0; i < 2; i += 1) {
    nextCursor = getNextWeekKey(nextCursor);
    nextWeeks.push(nextCursor);
  }

  const weekKeys = [...previousWeeks, currentWeekKey, ...nextWeeks].filter(Boolean);
  const weekStartDayKeys = weekKeys.map((weekKey) => getWeekStartDayKey(weekKey));
  const dayKeys = weekStartDayKeys.flatMap((weekStartDayKey) =>
    Array.from({ length: 7 }, (_, index) => addDaysToDayKey(weekStartDayKey, index))
  );

  return {
    currentWeekKey,
    dayKeys,
    timeZone,
    todayKey,
    weekKeys,
    weekStartDayKeys,
  };
}

export function getMonthCalendar(monthKeyInput, timeZoneInput = "UTC", now = new Date()) {
  const timeZone = resolveTimeZone(timeZoneInput);
  const currentMonthKey = getMonthKeyForDate(now, timeZone);
  const todayKey = getDayKeyForDate(now, timeZone);
  const monthKey = parseMonthKey(monthKeyInput) ? monthKeyInput : currentMonthKey;
  const parsedMonth = parseMonthKey(monthKey);

  if (!parsedMonth) {
    return {
      currentMonthKey,
      monthDayKeys: [],
      monthKey: currentMonthKey,
      monthLabel: formatMonthLabel(currentMonthKey),
      timeZone,
      todayKey,
      weekRows: [],
    };
  }

  const firstOfMonth = new Date(Date.UTC(parsedMonth.year, parsedMonth.month - 1, 1, 12));
  const daysInMonth = new Date(Date.UTC(parsedMonth.year, parsedMonth.month, 0, 12)).getUTCDate();
  const leadingEmptyDays = (firstOfMonth.getUTCDay() + 6) % 7;
  const monthDayKeys = Array.from(
    { length: daysInMonth },
    (_, index) => `${parsedMonth.year}-${pad2(parsedMonth.month)}-${pad2(index + 1)}`
  );
  const totalCells = Math.ceil((leadingEmptyDays + daysInMonth) / 7) * 7;
  const weekRows = [];

  for (let cellIndex = 0; cellIndex < totalCells; cellIndex += 7) {
    const row = Array.from({ length: 7 }, (_, offset) => {
      const dayOfMonth = cellIndex + offset - leadingEmptyDays + 1;
      if (dayOfMonth < 1 || dayOfMonth > daysInMonth) {
        return null;
      }
      return monthDayKeys[dayOfMonth - 1];
    });
    weekRows.push(row);
  }

  return {
    currentMonthKey,
    monthDayKeys,
    monthKey,
    monthLabel: formatMonthLabel(monthKey),
    timeZone,
    todayKey,
    weekRows,
  };
}

export function isStreakStillLive(lastActiveWeekKey, currentWeekKey) {
  if (!lastActiveWeekKey || !currentWeekKey) {
    return false;
  }

  if (lastActiveWeekKey === currentWeekKey) {
    return true;
  }

  return lastActiveWeekKey === getPreviousWeekKey(currentWeekKey);
}

function getInclusiveWeekCount(startWeekKey, endWeekKey) {
  if (!startWeekKey || !endWeekKey || compareWeekKeys(startWeekKey, endWeekKey) > 0) {
    return 0;
  }

  let count = 0;
  let cursor = startWeekKey;
  let guard = 0;

  while (cursor && guard < 200) {
    count += 1;
    if (cursor === endWeekKey) {
      return count;
    }
    cursor = getNextWeekKey(cursor);
    guard += 1;
  }

  return 0;
}

export function getStoredStreakWeeks(summaryData) {
  const streakWeeks = Number(summaryData?.streakLengthWeeks ?? summaryData?.currentStreakWeeks ?? 0);
  if (Number.isFinite(streakWeeks) && streakWeeks > 0) {
    return streakWeeks;
  }

  return getInclusiveWeekCount(summaryData?.streakStartWeekKey, summaryData?.lastActiveWeekKey);
}

export function getDerivedStreakState(summaryData, currentWeekKey) {
  const isLive = isStreakStillLive(summaryData?.lastActiveWeekKey, currentWeekKey);
  const storedStreakWeeks = getStoredStreakWeeks(summaryData);

  return {
    currentWeekKey,
    isLive,
    needsActivityThisWeek: summaryData?.lastActiveWeekKey !== currentWeekKey,
    storedStreakWeeks,
    displayedCurrentStreakWeeks: isLive ? storedStreakWeeks : 0,
  };
}

export function getDisplayedCurrentStreakWeeks(summaryData, currentWeekKey) {
  return getDerivedStreakState(summaryData, currentWeekKey).displayedCurrentStreakWeeks;
}

export function getRaceStreakAccessState(raceData, nowMs = Date.now()) {
  const raceStartString = raceData?.raceInfo?.raceDateStart;
  const raceEndString = raceData?.raceInfo?.raceDateEnd;

  if (!raceEndString) {
    return "none";
  }

  const raceEndMs = new Date(raceEndString).getTime();
  if (!Number.isFinite(raceEndMs)) {
    return "none";
  }

  if (raceEndMs >= nowMs) {
    if (raceStartString) {
      const raceStartMs = new Date(raceStartString).getTime();
      if (Number.isFinite(raceStartMs) && nowMs < raceStartMs) {
        return "none";
      }
    }
    return "active";
  }

  if (raceEndMs + STREAKS_GRACE_PERIOD_MS > nowMs) {
    return "grace";
  }

  return "none";
}

export function getRaceStreakUiState(raceData, nowMs = Date.now()) {
  const accessState = getRaceStreakAccessState(raceData, nowMs);
  if (accessState !== "none") {
    return accessState;
  }

  const raceStartString = raceData?.raceInfo?.raceDateStart;
  const raceEndString = raceData?.raceInfo?.raceDateEnd;
  if (!raceStartString || !raceEndString) {
    return "none";
  }

  const raceStartMs = new Date(raceStartString).getTime();
  const raceEndMs = new Date(raceEndString).getTime();
  if (!Number.isFinite(raceStartMs) || !Number.isFinite(raceEndMs)) {
    return "none";
  }

  return nowMs < raceStartMs ? "upcoming" : "none";
}
